import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  AccountCircle,
  PersonOutline,
  BusinessCenter,
} from '@mui/icons-material';
import { signupWithAuth0, loginWithGoogle } from '../../services/apiService';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { MuiTelInput } from 'mui-tel-input';

export function SignupForm() {
  const [formData, setFormData] = useState({
    signupFullName: '',
    signupEmail: '',
    signupUsername: '',
    signupMobile: '',
    signupPassword: '',
    signupAccountType: 'individual',
    signupCompanyName: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handlePasswordVisibility = () => setShowPassword((prev) => !prev);

  const validateFullName = (fullName) => {
    if (!fullName || !fullName.trim()) return 'Full name is required';
    if (fullName.trim().length < 3) return 'Please enter a valid full name';
    return '';
  };

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(email)) return 'Invalid email format';
    return '';
  };

  const validateUsername = (username) => {
    if (!username) return 'Username is required';
    return '';
  };

  const validateMobile = (mobile) => {
    if (!mobile) return 'Mobile number is required';
    const cleanNumber = mobile.replace(/\D/g, '');
    if (cleanNumber.length < 7) return 'Invalid mobile number format';
    return '';
  };

  const validateCompanyName = (companyName, accountType) => {
    const trimmedCompany = (companyName || '').trim();
    if (accountType === 'business' && !trimmedCompany) {
      return 'Company name is required for business accounts';
    }
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password))
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    return '';
  };

  const validateSignup = () => {
    const newErrors = {};
    newErrors.signupFullName = validateFullName(formData.signupFullName);
    newErrors.signupEmail = validateEmail(formData.signupEmail);
    newErrors.signupUsername = validateUsername(formData.signupUsername);
    newErrors.signupMobile = validateMobile(formData.signupMobile);
    newErrors.signupPassword = validatePassword(formData.signupPassword);
    const companyError = validateCompanyName(
      formData.signupCompanyName,
      formData.signupAccountType,
    );
    if (companyError) {
      newErrors.signupCompanyName = companyError;
    }

    // Remove empty error messages
    Object.keys(newErrors).forEach((key) => {
      if (!newErrors[key]) delete newErrors[key];
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    if (!validateSignup()) return;

    try {
      const trimmedFullName = formData.signupFullName.trim();
      const trimmedCompanyName = formData.signupCompanyName.trim();

      const signupResult = await signupWithAuth0(
        formData.signupEmail,
        formData.signupPassword,
        formData.signupUsername,
        trimmedFullName,
        formData.signupMobile,
        formData.signupAccountType,
        trimmedCompanyName,
      );

      console.log('Signup result:', signupResult);

      if (signupResult?.requiresEmailVerification) {
        console.log('Email verification required - showing message');
        toast.success(
          'Account created successfully! Please check your email to verify your account before logging in.',
          {
            autoClose: 5000,
            position: 'top-center',
            hideProgressBar: false,
          },
        );
        setTimeout(() => {
          navigate('/login');
        }, 4000);
      } else {
        toast.success(
          'Account created successfully! Please log in to continue.',
          {
            autoClose: 3000,
            position: 'top-center',
            hideProgressBar: false,
          },
        );
        setTimeout(() => {
          navigate('/login');
        }, 4000);
      }
    } catch (error) {
      console.log('Signup error:', error.response?.data); // Debug log

      const errorResponse = error.response?.data;
      const errorCode = errorResponse?.error || errorResponse?.code;
      const errorDescription =
        errorResponse?.error_description ||
        errorResponse?.description ||
        errorResponse?.message;
      const errorMessage =
        errorDescription || error.message || 'An error occurred during signup';

      // Check for duplicate email/user exists errors
      const isDuplicateEmail =
        errorCode === 'user_exists' ||
        errorMessage?.toLowerCase().includes('already exists') ||
        errorMessage?.toLowerCase().includes('user exists') ||
        errorMessage?.toLowerCase().includes('email already') ||
        errorMessage?.toLowerCase().includes('already registered') ||
        errorMessage?.toLowerCase().includes('duplicate') ||
        (errorCode === 'invalid_signup' &&
          errorMessage?.toLowerCase().includes('email')) ||
        // Check if invalid_signup with identifierType === "email" (likely duplicate email)
        (errorResponse?.code === 'invalid_signup' &&
          errorResponse?.data?.identifierType === 'email');

      // Handle duplicate email/user exists errors first (most common case)
      if (isDuplicateEmail) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          signupEmail:
            'This email is already registered. Please use a different email or try logging in.',
        }));
        toast.error(
          'This email is already registered. Please use a different email or try logging in.',
          { autoClose: 3000 },
        );
      }
      // Handle specific backend validation errors
      else if (errorResponse?.code === 'invalid_signup') {
        const errorData = errorResponse;
        const backendErrors = {};

        // Handle username validation error specifically
        if (errorData.data?.identifierType === 'username') {
          backendErrors.signupUsername =
            errorData.description || 'Invalid username';
        }
        // Handle email validation error (for non-duplicate cases)
        else if (errorData.data?.identifierType === 'email') {
          backendErrors.signupEmail = errorData.description || 'Invalid email';
        }
        // Handle other field-specific errors if needed
        else {
          // Generic error for unknown field types
          toast.error('Signup failed: ' + errorData.description, {
            autoClose: 3000,
          });
          return;
        }

        // Set the specific field errors
        setErrors((prevErrors) => ({
          ...prevErrors,
          ...backendErrors,
        }));

        // Show a toast for additional feedback
        toast.error('Please fix the validation errors and try again', {
          autoClose: 3000,
        });
      }
      // Handle other types of errors
      else {
        // Try to extract a meaningful error message
        const displayMessage =
          errorMessage || 'An error occurred during signup';
        toast.error('Signup failed: ' + displayMessage, { autoClose: 3000 });
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'signupAccountType') {
      setFormData((prev) => ({
        ...prev,
        signupAccountType: value,
        signupCompanyName: value === 'business' ? prev.signupCompanyName : '',
      }));

      setErrors((prev) => {
        const updatedErrors = { ...prev };
        if (value === 'business') {
          const companyError = validateCompanyName(
            formData.signupCompanyName,
            value,
          );
          if (companyError) {
            updatedErrors.signupCompanyName = companyError;
          } else {
            delete updatedErrors.signupCompanyName;
          }
        } else {
          delete updatedErrors.signupCompanyName;
        }
        return updatedErrors;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation
    let fieldError = '';
    switch (name) {
      case 'signupFullName':
        fieldError = validateFullName(value);
        break;
      case 'signupEmail':
        fieldError = validateEmail(value);
        break;
      case 'signupUsername':
        fieldError = validateUsername(value);
        break;
      case 'signupMobile':
        fieldError = validateMobile(value);
        break;
      case 'signupCompanyName':
        fieldError = validateCompanyName(value, formData.signupAccountType);
        break;
      case 'signupPassword':
        fieldError = validatePassword(value);
        break;
      default:
        break;
    }

    // Update errors state for the specific field
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError || undefined, // Remove error if empty
    }));

    // Clean up undefined errors
    if (!fieldError) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, signupMobile: value }));

    const fieldError = validateMobile(value);

    setErrors((prev) => ({
      ...prev,
      signupMobile: fieldError || undefined,
    }));

    if (!fieldError) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.signupMobile;
        return newErrors;
      });
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const response = await loginWithGoogle();

      if (!response || !response.token) {
        throw new Error('No token received from Google sign-up.');
      }

      // ✅ Store token securely before redirecting
      Cookies.set('authToken', response.token, { expires: 1, secure: true });
      Cookies.set('userId', response.user.user_id, {
        expires: 1,
        secure: true,
      });
      Cookies.set('Org_id', response.user.primary_org_id, {
        expires: 1,
        secure: true,
      });
      Cookies.set('Role_id', response.user.role_id, {
        expires: 1,
        secure: true,
      });
      Cookies.set(
        'username',
        response.user.name ||
          response.user.username ||
          response.user.nickname ||
          response.user.email ||
          'Guest',
        { expires: 1, secure: true },
      );
      Cookies.set('userEmail', response.user.email || '', {
        expires: 1,
        secure: true,
      });

      console.log('🔹 Token stored successfully');
      console.log('🔹 Redirecting to dashboard after token storage');
      toast.success('Signup successful!', { autoClose: 2000 });
      setTimeout(() => {
        navigate('/userdashboard'); // ✅ Ensures token is stored before navigation
      }, 2000); // Small delay to ensure storage
    } catch (error) {
      alert('Google Sign-up/Login failed: ' + error.message);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="h4"
        fontWeight={600}
        gutterBottom
        sx={{
          color: '#1a1a1a',
          textAlign: 'center',
          mb: { xs: 1, sm: 1, md: 1.5 },
          fontSize: {
            xs: '1.5rem',
            sm: '1.75rem',
            md: '2rem',
          },
        }}
      >
        Sign Up
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{
          color: '#666666',
          textAlign: 'center',
          mb: { xs: 2, sm: 2.5, md: 3 },
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
        }}
      >
        Welcome! Create your account to get started.
      </Typography>

      <Box
        component="form"
        onSubmit={handleSignup}
        sx={{ mt: { xs: 0.5, sm: 1, md: 1.5 } }}
      >
        <TextField
          fullWidth
          name="signupFullName"
          placeholder="Full Name"
          value={formData.signupFullName}
          onChange={handleChange}
          error={!!errors.signupFullName}
          helperText={errors.signupFullName}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutline
                  sx={{
                    color: '#9ca3af',
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: { xs: 1, sm: 1.5 },
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
              backgroundColor: 'white',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              height: { xs: '36px', sm: '30px' },
              padding: { xs: '6px 14px', sm: '8px 14px' },
              color: '#1f2937',
              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            },
          }}
        />

        <TextField
          fullWidth
          name="signupUsername"
          placeholder="User Name"
          value={formData.signupUsername}
          onChange={handleChange}
          error={!!errors.signupUsername}
          helperText={errors.signupUsername}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AccountCircle
                  sx={{
                    color: '#9ca3af',
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: { xs: 1, sm: 1.5 },
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
              backgroundColor: 'white',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              height: { xs: '36px', sm: '30px' },
              padding: { xs: '6px 14px', sm: '8px 14px' },
              color: '#1f2937',
              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            },
          }}
        />

        <TextField
          fullWidth
          name="signupEmail"
          placeholder="Email"
          value={formData.signupEmail}
          onChange={handleChange}
          error={!!errors.signupEmail}
          helperText={errors.signupEmail}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlined
                  sx={{
                    color: '#9ca3af',
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: { xs: 1, sm: 1.5 },
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
              backgroundColor: 'white',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              height: { xs: '36px', sm: '30px' },
              padding: { xs: '6px 14px', sm: '8px 14px' },
              color: '#1f2937',
              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            },
          }}
        />

        <FormControl
          component="fieldset"
          sx={{
            mb: { xs: 1, sm: 1.5 },
            '& .MuiFormLabel-root': {
              color: '#6b7280',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              '&.Mui-focused': {
                color: '#3b82f6',
              },
            },
          }}
        >
          <FormLabel component="legend">Account type</FormLabel>
          <RadioGroup
            row
            aria-label="Account type"
            name="signupAccountType"
            value={formData.signupAccountType}
            onChange={handleChange}
            sx={{
              '& .MuiFormControlLabel-root': {
                marginRight: { xs: 1, sm: 2 },
              },
              '& .MuiRadio-root': {
                color: '#9ca3af',
                '&.Mui-checked': {
                  color: '#3b82f6',
                },
              },
            }}
          >
            <FormControlLabel
              value="individual"
              control={<Radio />}
              label="Individual"
            />
            <FormControlLabel
              value="business"
              control={<Radio />}
              label="Business"
            />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          name="signupCompanyName"
          placeholder={
            formData.signupAccountType === 'business'
              ? 'Company Name'
              : 'Company Name (optional)'
          }
          value={formData.signupCompanyName}
          onChange={handleChange}
          error={!!errors.signupCompanyName}
          helperText={
            errors.signupCompanyName ||
            (formData.signupAccountType === 'individual'
              ? 'Optional for individual accounts'
              : '')
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BusinessCenter
                  sx={{
                    color: '#9ca3af',
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: { xs: 1, sm: 1.5 },
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
              backgroundColor: 'white',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              height: { xs: '36px', sm: '30px' },
              padding: { xs: '6px 14px', sm: '8px 14px' },
              color: '#1f2937',
              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            },
          }}
        />

        <MuiTelInput
          fullWidth
          value={formData.signupMobile}
          onChange={handlePhoneChange}
          error={!!errors.signupMobile}
          helperText={errors.signupMobile}
          defaultCountry="US"
          placeholder="Mobile number"
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
                width: 'auto',
                minWidth: 200,
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
          sx={{
            mb: { xs: 1, sm: 1.5 },
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
              backgroundColor: 'white',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              height: { xs: '36px', sm: '30px' },
              padding: { xs: '6px 14px', sm: '8px 14px' },
              color: '#1f2937',
              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            },
            '& .MuiTelInput-Flag': {
              marginRight: '8px',
            },
            '& .MuiTelInput-IconButton': {
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
        />

        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          name="signupPassword"
          placeholder="Password"
          value={formData.signupPassword}
          onChange={handleChange}
          error={!!errors.signupPassword}
          helperText={errors.signupPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handlePasswordVisibility} edge="end">
                  {showPassword ? (
                    <VisibilityOff
                      sx={{
                        color: '#9ca3af',
                        fontSize: { xs: '1.2rem', sm: '1.4rem' },
                      }}
                    />
                  ) : (
                    <Visibility
                      sx={{
                        color: '#9ca3af',
                        fontSize: { xs: '1.2rem', sm: '1.4rem' },
                      }}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: { xs: 1.5, sm: 2, md: 2.5 },
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
              backgroundColor: 'white',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              height: { xs: '36px', sm: '30px' },
              padding: { xs: '6px 14px', sm: '8px 14px' },
              color: '#1f2937',
              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            height: { xs: '44px', sm: '48px', md: '50px' },
            borderRadius: { xs: '8px', sm: '10px', md: '12px' },
            fontWeight: 600,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            mb: { xs: 1, sm: 1.5 },
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: 'none',
            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(1px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          SIGN UP
        </Button>

        <Box sx={{ textAlign: 'center', mb: { xs: 1, sm: 1.5 } }}>
          <Typography
            variant="body2"
            sx={{
              color: '#6b7280',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
          >
            Already have an account?{' '}
            <Link
              onClick={() => navigate('/login')}
              sx={{
                cursor: 'pointer',
                color: '#3b82f6',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': {
                  color: '#2563eb',
                  textDecoration: 'underline',
                },
              }}
            >
              Login
            </Link>
          </Typography>
        </Box>

        <Divider
          sx={{
            mb: { xs: 1.5, sm: 2, md: 2.5 },
            color: '#6b7280',
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            '&::before, &::after': {
              borderColor: '#e5e7eb',
            },
          }}
        >
          Or
        </Divider>

        <Button
          variant="outlined"
          fullWidth
          onClick={handleGoogleSignup}
          sx={{
            height: { xs: '44px', sm: '48px', md: '50px' },
            borderRadius: { xs: '8px', sm: '10px', md: '12px' },
            fontSize: { xs: '0.875rem', sm: '0.9rem' },
            fontWeight: 500,
            color: '#374151',
            borderColor: '#d1d5db',
            backgroundColor: 'white',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#f9fafb',
              borderColor: '#9ca3af',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 28 28"
            fill="none"
            style={{ marginRight: 8 }}
          >
            <path
              d="M27.5007 14.0819C27.5007 12.9493 27.4088 12.1227 27.2099 11.2656H14.2764V16.3777H21.868C21.715 17.6482 20.8885 19.5614 19.0518 20.847L19.026 21.0182L23.1154 24.1861L23.3987 24.2144C26.0006 21.8114 27.5007 18.2757 27.5007 14.0819Z"
              fill="#4285F4"
            />
            <path
              d="M14.2764 27.5508C17.9957 27.5508 21.1181 26.3263 23.3987 24.2142L19.0518 20.8468C17.8886 21.658 16.3274 22.2243 14.2764 22.2243C10.6337 22.2243 7.54189 19.8214 6.43976 16.5L6.27821 16.5137L2.02607 19.8045L1.97046 19.9591C4.23571 24.459 8.88871 27.5508 14.2764 27.5508Z"
              fill="#34A853"
            />
            <path
              d="M6.43936 16.5005C6.14855 15.6434 5.98025 14.725 5.98025 13.776C5.98025 12.827 6.14855 11.9087 6.42406 11.0516L6.41636 10.869L2.11093 7.52539L1.97006 7.59239C1.03644 9.45974 0.500732 11.5567 0.500732 13.776C0.500732 15.9954 1.03644 18.0922 1.97006 19.9596L6.43936 16.5005Z"
              fill="#FBBC05"
            />
            <path
              d="M14.2764 5.32642C16.8631 5.32642 18.6079 6.44374 19.6029 7.37746L23.4905 3.58158C21.1029 1.36223 17.9957 0 14.2764 0C8.88871 0 4.23571 3.09177 1.97046 7.59167L6.42446 11.0508C7.54189 7.72947 10.6337 5.32642 14.2764 5.32642Z"
              fill="#EB4335"
            />
          </svg>
          Continue with Google
        </Button>
      </Box>
    </Box>
  );
}

export default SignupForm;
